/**
 * Message fragments browsers and Vite use when a lazy chunk or its preloaded
 * dependency cannot be loaded. A failed `import()` carries no HTTP status, so
 * the message is the only signal that the error is a chunk failure at all.
 */
const CHUNK_LOAD_MESSAGE_PATTERNS: readonly RegExp[] = [
  // Chromium
  /Failed to fetch dynamically imported module/i,
  // Firefox
  /error loading dynamically imported module/i,
  // Safari (no URL in the message)
  /Importing a module script failed/i,
  // Vite's CSS preload helper
  /Unable to preload CSS for/i,
  // Webpack-style names, kept for MFE bundles that may surface them
  /Loading (CSS )?chunk [\w-]+ failed/i,
  // The host answered with HTML (for example an SPA fallback) for a module URL
  /Expected a JavaScript.*module script but the server responded with a MIME type/i,
];

/** Captures the URL that Chromium, Firefox and Vite append to the message. */
const CHUNK_URL_PATTERN =
  /(?:dynamically imported module:?|preload CSS for)\s+(\S+)/i;

const getErrorMessage = (error: unknown): string | undefined => {
  if (typeof error === 'string') {
    return error;
  }
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const { message } = error;
    return typeof message === 'string' ? message : undefined;
  }
  return undefined;
};

/**
 * Whether `error` is the failure of a lazy chunk (route module, dynamic import
 * or Vite preload dependency) rather than an ordinary runtime error.
 *
 * Accepts `unknown` because router errors may be plain objects or
 * `ErrorResponse`s instead of `Error` instances.
 */
export const isChunkLoadError = (error: unknown): boolean => {
  if (
    typeof error === 'object' &&
    error !== null &&
    'name' in error &&
    error.name === 'ChunkLoadError'
  ) {
    return true;
  }

  const message = getErrorMessage(error);
  return (
    message !== undefined &&
    CHUNK_LOAD_MESSAGE_PATTERNS.some((pattern) => pattern.test(message))
  );
};

/**
 * Extracts the absolute, same-origin URL of the chunk that failed to load.
 *
 * Returns `undefined` when the browser omits the URL (Safari), when the URL is
 * malformed, or when it points at another origin. A cross-origin probe would
 * fail on CORS and look like a network error, so those are left unclassified.
 */
export const getChunkLoadErrorUrl = (error: unknown): string | undefined => {
  const raw = getErrorMessage(error)?.match(CHUNK_URL_PATTERN)?.[1];
  if (!raw) {
    return undefined;
  }

  try {
    const url = new URL(raw, window.location.href);
    return url.origin === window.location.origin ? url.href : undefined;
  } catch {
    return undefined;
  }
};
